import { type FC } from "react";
import { formatFileSize } from "../utils/fileUtil";

export interface FileUpload {
  file: File | null;
  id: string | null;
  error: string | null;
  progress: number;
}

interface FilePreviewProps {
  fileUpload: FileUpload;
  onRemove: () => void;
}

const FilePreview: FC<FilePreviewProps> = ({ fileUpload, onRemove }) => {
  const strokeDashoffset = 138.16 - (fileUpload.progress / 100) * 138.16;

  return (
    <div className="flex max-w-64 relative" data-testid={`file-preview`}>
      <div className="flex w-14 shrink-0 items-center justify-center rounded-l-lg bg-lastbot-primary text-sm font-medium text-lastbot-on-primary relative">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 -960 960 960"
          fill="currentColor"
          className="size-5"
        >
          <path d="M240-80q-33 0-56.5-23.5T160-160v-640q0-33 23.5-56.5T240-880h320l240 240v480q0 33-23.5 56.5T720-80H240Zm280-520v-200H240v640h480v-440H520ZM240-800v200-200 640-640Z" />
        </svg>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 56 56"
          className={`size-11 absolute -rotate-90 transition-opacity duration-700 ${fileUpload.progress === 100 ? "opacity-0" : ""}`}
        >
          <circle
            r="22"
            cx="28"
            cy="28"
            fill="transparent"
            stroke="#000"
            strokeWidth="4"
            strokeDasharray="138.16px"
            strokeDashoffset="0"
            className="opacity-25"
          />
          <circle
            r="22"
            cx="28"
            cy="28"
            fill="transparent"
            stroke="#71a0ff"
            strokeWidth="4"
            strokeDasharray="138.16px"
            strokeDashoffset={`${strokeDashoffset}px`}
            strokeLinecap="butt"
            className="transition-[stroke-dashoffset] duration-300"
          />
        </svg>
      </div>
      <div className="flex-1 flex items-center truncate rounded-r-lg border-b border-r border-t border-lastbot-border bg-lastbot-surface">
        <div className="flex-1 truncate px-3 py-[7px] text-sm">
          <div className="font-medium text-lastbot-on-surface truncate">{fileUpload.file?.name}</div>
          <div className="text-lastbot-icon-muted">{formatFileSize(fileUpload.file?.size ?? 0)}</div>
        </div>
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="cursor-pointer absolute top-1 right-1 -translate-y-1/2 translate-x-1/2 rounded-full bg-lastbot-surface-inverse-strong"
        data-testid={`file-preview-remove`}
      >
        <svg viewBox="0 0 14 14" className="size-4 stroke-lastbot-on-inverse">
          <path d="M4 4l6 6m0-6l-6 6" />
        </svg>
        <span className="absolute -inset-1" />
      </button>
    </div>
  );
};

export default FilePreview;
